import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { 
  Video, Eye, EyeOff, Loader2, ArrowLeft, Lock, Mail, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CreatorPortalLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [tempToken, setTempToken] = useState('');

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('creatorPortalToken');
    if (token) {
      navigate('/creator/portal');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/creator-portal/auth/login`, {
        email: email.toLowerCase().trim(),
        password
      });

      if (res.data.success) {
        if (res.data.must_change_password) {
          // User must change password before accessing dashboard
          setMustChangePassword(true);
          setTempToken(res.data.token);
          // Store creator info temporarily
          localStorage.setItem('creatorPortalTemp', JSON.stringify(res.data.creator));
        } else {
          // Normal login - store token and redirect
          localStorage.setItem('creatorPortalToken', res.data.token);
          localStorage.setItem('creatorPortalUser', JSON.stringify(res.data.creator));
          navigate('/creator/portal');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setChangingPassword(true);

    try {
      await axios.post(
        `${API}/api/creator-portal/auth/change-password`,
        {
          current_password: password,
          new_password: newPassword
        },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      // Password changed successfully - now fully logged in
      const creatorInfo = JSON.parse(localStorage.getItem('creatorPortalTemp') || '{}');
      localStorage.removeItem('creatorPortalTemp');
      localStorage.setItem('creatorPortalToken', tempToken);
      localStorage.setItem('creatorPortalUser', JSON.stringify(creatorInfo));
      
      navigate('/creator/portal');
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Password change form
  if (mustChangePassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex flex-col">
        {/* Header with Logo */}
        <header className="py-4 px-6">
          <a href="/vod" className="inline-block">
            <img 
              src="/ltv-vod-logo.png" 
              alt="LTV VOD" 
              className="h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </a>
        </header>
        
        {/* Password Change Form */}
        <div className="flex-1 flex items-center justify-center px-4 pb-12">
          <Card className="w-full max-w-md bg-gray-900/80 border-purple-500/30 backdrop-blur">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-4">
                <Lock className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl text-white">Change Your Password</CardTitle>
              <CardDescription className="text-gray-400">
                For security, please set a new password before continuing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-gray-300">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="bg-white/5 border-white/10 text-white pr-10"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Minimum 8 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-white/5 border-white/10 text-white"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={changingPassword}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  'Set New Password & Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  // Login form
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex flex-col">
      {/* Header with Logo */}
      <header className="py-4 px-6">
        <a href="/vod" className="inline-block">
          <img 
            src="/ltv-vod-logo.png" 
            alt="LTV VOD" 
            className="h-10"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </a>
      </header>
      
      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <Card className="w-full max-w-md bg-gray-900/80 border-purple-500/30 backdrop-blur">
          <CardHeader className="text-center">
            <button
              onClick={() => navigate('/vod')}
              className="absolute top-4 left-4 text-gray-400 hover:text-white flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 overflow-hidden">
              <img 
                src="/ltv-vod-logo.png" 
                alt="LTV VOD" 
                className="h-16 w-auto"
                onError={(e) => { e.target.parentElement.innerHTML = '<div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center"><svg class="h-8 w-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg></div>'; }}
              />
            </div>
            <CardTitle className="text-2xl text-white">Creator Portal</CardTitle>
            <CardDescription className="text-gray-400">
              Sign in to view your channel analytics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-white/5 border-white/10 text-white pl-10"
                  required
                  data-testid="creator-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-white/5 border-white/10 text-white pl-10 pr-10"
                  required
                  data-testid="creator-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
              data-testid="creator-login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Don't have an account? Contact your administrator.
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default CreatorPortalLogin;
